import { useState, useEffect, useMemo } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { TravelRequestBadge } from '@/lib/requests/TravelRequestBadge';
import { UserAvatar } from '@/components/ui/user-avatar';
import { DashboardTravelRequest, IDashboardTravelRequestData } from '@/lib/models/dashboard';
import { LinkTemplateMode } from '@/hooks/useOrphanItineraries';
import { addDays, inclusiveDayCount } from '@/components/utils/dateUtils';
import { formatDayDate } from '@/lib/builder/components/day-labels';
import { Stepper } from '@/components/ui/stepper';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, ArrowLeft, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/components/utils/utils';

interface LinkToTravelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (travelRequestId: string, options: { mode: LinkTemplateMode; startDayOffset: number }) => Promise<void>;
  itineraryId: string;
  itineraryTitle: string;
  /** Giorni del template: è il termine di paragone con la durata della richiesta. */
  templateDays: number;
}

export const LinkToTravelRequestModal = ({
  isOpen,
  onClose,
  onLink,
  itineraryId,
  itineraryTitle,
  templateDays,
}: LinkToTravelRequestModalProps) => {
  const { t, language } = useI18n();
  const locale = language || 'it-IT';

  const [step, setStep] = useState<1 | 2>(1);
  const [travelRequests, setTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [mode, setMode] = useState<LinkTemplateMode>('keep');
  const [startDayOffset, setStartDayOffset] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchTravelRequests();
    }
  }, [isOpen]);

  // Cambiando richiesta cambiano anche le opzioni disponibili: ripartire dai default
  // evita di restare su una scelta che per la nuova richiesta non esiste più.
  useEffect(() => {
    setMode('keep');
    setStartDayOffset(0);
  }, [selectedRequestId]);

  const fetchTravelRequests = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_dashboard_travel_requests', {
        p_status_exclude: null,
      });

      if (error) {
        toast({
          title: t('common.error'),
          description: t('playground.loadTravelRequestsError'),
          variant: 'destructive',
        });
        return;
      }

      // Map to DashboardTravelRequest and filter for pending/assigned only
      const mappedTravelRequests = ((data as IDashboardTravelRequestData[]) || [])
        .map((req: IDashboardTravelRequestData) => new DashboardTravelRequest(req))
        .filter((req) => req.status === 'pending' || req.status === 'assigned');

      setTravelRequests(mappedTravelRequests);
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedRequest = travelRequests.find((tr) => tr.id === selectedRequestId) || null;

  /**
   * Confronto fra la durata del template e quella della richiesta: è il cuore
   * del secondo step, da cui dipendono avviso, opzioni e anteprima delle date.
   */
  const comparison = useMemo(() => {
    const start = selectedRequest?.travelStartDate || null;
    const end = selectedRequest?.travelEndDate || null;
    const requestDays = start && end ? inclusiveDayCount(start, end) : null;

    const maxOffset = requestDays ? Math.max(requestDays - templateDays, 0) : 0;
    const offset = Math.min(startDayOffset, maxOffset);

    const effectiveDays = (() => {
      if (!requestDays) return templateDays;
      if (mode === 'truncate') return Math.max(Math.min(templateDays, requestDays - offset), 1);
      if (mode === 'extend') return Math.max(templateDays, requestDays - offset);
      return templateDays;
    })();

    const resultStart = start ? addDays(start, offset) : null;
    const resultEnd = resultStart ? addDays(resultStart, effectiveDays - 1) : null;

    return {
      requestStart: start,
      requestEnd: end,
      requestDays,
      maxOffset,
      offset,
      effectiveDays,
      resultStart,
      resultEnd,
      // Quanti giorni del template escono dalla finestra della richiesta.
      exceedingDays: requestDays ? Math.max(templateDays - (requestDays - offset), 0) : 0,
      // Quanti giorni della richiesta resterebbero scoperti.
      uncoveredDays: requestDays ? Math.max(requestDays - offset - templateDays, 0) : 0,
    };
  }, [selectedRequest, templateDays, mode, startDayOffset]);

  const handleLink = async () => {
    if (!selectedRequestId) {
      return;
    }

    setIsLinking(true);
    try {
      await onLink(selectedRequestId, { mode, startDayOffset: comparison.offset });
      onClose();
    } catch (error) {
      // Error handled by onLink
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedRequestId(null);
    setMode('keep');
    setStartDayOffset(0);
    onClose();
  };

  /** Opzioni offerte allo step 2, diverse a seconda di chi è più lungo. */
  const modeOptions: { value: LinkTemplateMode; label: string; description: string }[] = (() => {
    if (comparison.exceedingDays > 0) {
      return [
        {
          value: 'keep',
          label: t('playground.linkModeKeep'),
          description: t('playground.linkModeKeepLonger', { count: comparison.exceedingDays }),
        },
        {
          value: 'truncate',
          label: t('playground.linkModeTruncate'),
          description: t('playground.linkModeTruncateDesc', { count: comparison.exceedingDays }),
        },
      ];
    }
    if (comparison.uncoveredDays > 0) {
      return [
        {
          value: 'keep',
          label: t('playground.linkModeKeep'),
          description: t('playground.linkModeKeepShorter', { count: comparison.uncoveredDays }),
        },
        {
          value: 'extend',
          label: t('playground.linkModeExtend'),
          description: t('playground.linkModeExtendDesc', { count: comparison.uncoveredDays }),
        },
      ];
    }
    return [];
  })();

  /**
   * Cambia quando cambia la scelta da fare: altre opzioni, o lo stesso scarto misurato
   * diversamente. Usata come `key` del gruppo, è ciò che fa ripartire l'animazione di
   * comparsa quando si sposta il giorno d'inizio.
   */
  const optionsSignature = `${comparison.exceedingDays}-${comparison.uncoveredDays}`;

  /**
   * C'è qualcosa da decidere solo se il template e la richiesta non combaciano: o si sceglie
   * come comportarsi con lo scarto, o si sceglie da che giorno partire.
   */
  const hasChoice = modeOptions.length > 0 || comparison.maxOffset > 0;

  /** Lo scarto fra template e richiesta, detto una volta sola sotto le due durate. */
  const mismatchMessage = comparison.exceedingDays > 0
    ? t('playground.mismatchLonger', { count: comparison.exceedingDays })
    : comparison.uncoveredDays > 0
      ? t('playground.mismatchShorter', { count: comparison.uncoveredDays })
      : t('playground.mismatchNone');

  // Stesso stepper dei flussi di condivisione (link e email).
  const steps = [
    { label: t('playground.stepSelectRequest') },
    { label: t('playground.stepReviewDays') },
  ];

  // Stesse colonne che si usano nelle altre tabelle del prodotto (link condivisi,
  // libreria): destinazione, persona, periodo, ospiti, stato.
  const columns: DataTableColumn<DashboardTravelRequest>[] = [
    {
      id: 'destination',
      // Senza larghezza: assorbe lo spazio residuo, come la colonna titolo altrove.
      header: t('travelRequest.tableDestination'),
      cellClassName: 'font-medium break-words',
      cell: (request) => request.destinations.join(', ') || '—',
    },
    {
      id: 'client',
      header: t('travelRequest.tableContact'),
      headClassName: 'w-[24%]',
      cell: (request) => (
        <UserAvatar
          expanded
          fullName={request.contactFullName}
          email={request.contactEmail}
          imageUrl={request.contact.avatarUrl}
          size="sm"
          showTooltip
        />
      ),
    },
    {
      id: 'dates',
      header: t('travelRequest.tableDates'),
      headClassName: 'w-[22%]',
      cell: (request) => (
        <div className="flex flex-col">
          <span>
            {formatDayDate(request.travelStartDate, locale)} – {formatDayDate(request.travelEndDate, locale)}
          </span>
          <span className="text-xs text-gray-500">
            {t('builder.durationDays', { count: request.totalDays })}
          </span>
        </div>
      ),
    },
    {
      id: 'guests',
      header: t('travelRequest.tablePeople'),
      headClassName: 'w-[10%]',
      cell: (request) => request.totalGuests || 1,
    },
    {
      id: 'status',
      header: t('travelRequest.tableStatus'),
      headClassName: 'w-[14%]',
      cell: (request) => <TravelRequestBadge travelRequest={request} variant="table" />,
    },
  ];

  // I pulsanti vanno nel footer del Modal, che li tiene fissi mentre il corpo scorre:
  // dentro il contenuto sparirebbero sotto la tabella delle richieste.
  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      {step === 1 ? (
        <>
          <Button variant="outline" onClick={handleClose} disabled={isLinking}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => setStep(2)} disabled={!selectedRequestId}>
            {t('common.next')}
          </Button>
        </>
      ) : (
        <>
          <Button variant="outline" onClick={() => setStep(1)} disabled={isLinking}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
          <Button onClick={handleLink} disabled={isLinking}>
            {isLinking ? t('playground.linking') : t('playground.linkButton')}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('playground.linkToTravelRequest')}
      size="lg"
      footer={footer}
    >
      <div className="space-y-4 p-6">
          <Stepper steps={steps} currentStep={step} />

          {/* Solo nello step 1: una volta scelta la richiesta, la riga serve al confronto
              delle durate e ripetere qui il template non aggiunge niente. */}
          {step === 1 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-900">
                <strong>{t('playground.linkingItinerary')}:</strong> {itineraryTitle}
                {' — '}
                {t('builder.durationDays', { count: templateDays })}
              </p>
              <p className="mt-0.5 text-xs leading-snug text-blue-700">{t('playground.linkExplanation')}</p>
            </div>
          )}

          {step === 1 ? (
            <div>
              <Label>{t('playground.selectTravelRequest')}</Label>

              <div className="mt-2 h-[45vh]">
                <DataTable<DashboardTravelRequest>
                  data={travelRequests}
                  columns={columns}
                  getRowId={(request) => request.id}
                  onRowClick={(request) => setSelectedRequestId(request.id)}
                  selectedRowId={selectedRequestId}
                  isLoading={isLoading}
                  pageSize={5}
                  tableClassName="min-w-[720px]"
                  emptyState={
                    <div className="text-center py-8 text-gray-500">
                      <p>{t('playground.noTravelRequests')}</p>
                      <p className="text-sm mt-1">{t('playground.noTravelRequestsDesc')}</p>
                    </div>
                  }
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Confronto durate: le due misure e, sotto, il loro scarto */}
              <div className="rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-3 p-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">{t('playground.templateDuration')}</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {t('builder.durationDays', { count: templateDays })}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">{t('playground.requestDuration')}</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {comparison.requestDays
                        ? t('builder.durationDays', { count: comparison.requestDays })
                        : '—'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDayDate(comparison.requestStart, locale)} – {formatDayDate(comparison.requestEnd, locale)}
                    </div>
                  </div>
                </div>

                {/* Lo scarto fra le due misure: è la conseguenza del confronto qui sopra,
                    non un avviso a sé stante. I due versi non pesano uguale: un template
                    più lungo mette in gioco attività che possono andare perse (warning),
                    una richiesta più lunga lascia solo giorni da riempire (info). */}
                <div
                  className={cn(
                    'flex items-center gap-2 border-t px-3 py-2 text-sm',
                    comparison.exceedingDays > 0
                      ? 'border-amber-200 bg-amber-50 text-amber-900'
                      : comparison.uncoveredDays > 0
                        ? 'border-blue-200 bg-blue-50 text-blue-900'
                        : 'border-green-200 bg-green-50 text-green-900',
                  )}
                >
                  {comparison.exceedingDays > 0 ? (
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  ) : comparison.uncoveredDays > 0 ? (
                    <Info className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  )}
                  {mismatchMessage}
                </div>
              </div>

              {/* Scelta e risultato affiancati: la scelta si legge insieme al suo effetto.
                  Su schermi stretti la griglia torna a una colonna. Senza scarto non c'è
                  né una scelta da fare né un risultato da anticipare: le date sarebbero
                  quelle della richiesta, già lette qui sopra. */}
              {hasChoice && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  {/* Giorno di partenza dentro la richiesta: solo se c'è margine.
                      Viene prima della scelta perché è la sua causa: spostare il giorno
                      d'inizio cambia lo scarto, e quindi le opzioni qui sotto. */}
                  {comparison.maxOffset > 0 && (
                    <div className="rounded-lg border border-gray-200 p-3">
                      <Label htmlFor="startDayOffset">{t('playground.startFromDay')}</Label>
                      <select
                        id="startDayOffset"
                        className="mt-2 w-full rounded-md border border-gray-200 bg-white p-2 text-sm"
                        value={comparison.offset}
                        onChange={(e) => setStartDayOffset(Number(e.target.value))}
                      >
                        {Array.from({ length: comparison.maxOffset + 1 }, (_, i) => (
                          <option key={i} value={i}>
                            {t('builder.dayNumber', { number: i + 1 })}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">{t('playground.startFromDayHelp')}</p>
                    </div>
                  )}

                  {modeOptions.length > 0 && (
                      <RadioGroup
                        value={mode}
                        onValueChange={(value) => setMode(value as LinkTemplateMode)}
                        // Le due scelte stanno in linea: metà della colonna ciascuna, cioè
                        // un quarto della modale. La `key` cambia quando cambia lo scarto,
                        // così l'animazione riparte a ogni cambio di giorno d'inizio.
                        key={optionsSignature}
                        className="grid animate-in grid-cols-2 gap-2 fade-in slide-in-from-top-1 duration-300"
                      >
                        {modeOptions.map((option) => (
                          <label
                            key={option.value}
                            htmlFor={`link-mode-${option.value}`}
                            className={cn(
                              'flex cursor-pointer items-start gap-2 rounded-lg border p-2 transition-colors',
                              mode === option.value ? 'border-gray-900 bg-white' : 'border-gray-200 hover:bg-gray-50',
                            )}
                          >
                            <RadioGroupItem value={option.value} id={`link-mode-${option.value}`} className="mt-0.5" />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                              <span className="block text-xs text-gray-600">{option.description}</span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                  )}
                </div>

                {/* Anteprima del risultato */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    {t('playground.resultPreview')}
                  </div>
                  <div className="text-gray-900">
                    {formatDayDate(comparison.resultStart, locale)} – {formatDayDate(comparison.resultEnd, locale)}
                    {' · '}
                    {t('builder.durationDays', { count: comparison.effectiveDays })}
                  </div>
                  {mode === 'keep' && comparison.exceedingDays > 0 && (
                    <div className="text-xs text-red-600 mt-1">
                      {t('playground.exceedingDaysNotice', { count: comparison.exceedingDays })}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
      </div>
    </Modal>
  );
};
